import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import {
  SubscriptionFeatureList,
  SubscriptionFeatureUpdate,
  SubscriptionFeatureUpdateOne,
} from "../../services/subscriptionFeature.services";
import {
  globalLoader,
  handleServerValidations,
} from "../../../../utils/commonfunction";
import Swal from "sweetalert2";
import { SWAL_SETTINGS } from "../../../../utils/Constants";
import Breadcrums from "../../common/breadcrumbs";
import Loader from "../../common/loader";

const emptyRule = (planSlug) => ({
  plan_slug: planSlug,
  allowed: false,
  quota_type: "none",
  limit: "",
});

const breadcrumbs = [
  { title: "Dashboard", url: "/admin/dashboard" },
  { title: "Subscription Features", url: "" },
];

const SubscriptionFeature = () => {
  const [initialData, setInitialData] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loader, setLoader] = useState(false);
  const [changesFlag, setChangesFlag] = useState(false);

  const loadData = () => {
    setLoader(true);
    SubscriptionFeatureList()
      .then((featuresRes) => {
        setInitialData(featuresRes?.data?.list || []);
        setPlans(featuresRes?.data?.plans || []);
        setLoader(false);
      })
      .catch(() => setLoader(false));
  };

  useEffect(() => {
    loadData();
  }, [changesFlag]);

  const getRule = (feature, planSlug) => {
    const found = (feature.plan_rules || []).find((r) => r.plan_slug === planSlug);
    return found || emptyRule(planSlug);
  };

  const updateRule = (featureIndex, planSlug, patch) => {
    const features = formik.values.features.map((f, i) => {
      if (i !== featureIndex) return f;
      const plan_rules = plans.map((plan) => {
        const current = getRule(f, plan.slug);
        if (plan.slug !== planSlug) return current;
        const next = { ...current, plan_slug: planSlug, ...patch };
        if (next.allowed === false) {
          next.quota_type = "none";
          next.limit = null;
        } else if (next.quota_type === "unlimited") {
          next.limit = null;
        }
        return next;
      });
      return { ...f, plan_rules };
    });
    formik.setFieldValue("features", features);
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: { features: initialData },
    onSubmit: (values, { setSubmitting }) => {
      globalLoader(true);
      const formData = new FormData();
      formData.append("features", JSON.stringify(values.features));
      SubscriptionFeatureUpdate(formData)
        .then((response) => {
          setSubmitting(false);
          globalLoader(false);
          if (response.success) {
            Swal.fire({ icon: "success", text: response.message, ...SWAL_SETTINGS });
            setChangesFlag((f) => !f);
          } else {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
          }
        })
        .catch(() => {
          setSubmitting(false);
          globalLoader(false);
        });
    },
  });

  const saveOne = (feature) => {
    globalLoader(true);
    SubscriptionFeatureUpdateOne({
      o_id: feature._id,
      name: feature.name,
      action_key: feature.action_key,
      plan_rules: JSON.stringify(feature.plan_rules),
    })
      .then((response) => {
        globalLoader(false);
        if (response.success) {
          Swal.fire({ icon: "success", text: response.message, ...SWAL_SETTINGS });
        } else {
          Swal.fire({
            icon: "error",
            text: handleServerValidations(response),
            ...SWAL_SETTINGS,
          });
        }
      })
      .catch(() => globalLoader(false));
  };

  return (
    <div className="mt-4">
      <Breadcrums data={breadcrumbs} />
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2>Feature Access</h2>
          <p className="text-muted mb-0">
            Change allow / unlimited / limited / count per plan. New enforceable
            features are added from backend when a new API is ready.
          </p>
        </div>
      </div>

      {loader ? (
        <Loader />
      ) : (
        <form onSubmit={formik.handleSubmit}>
          <div className="table-responsive">
            <table className="table table-bordered align-middle">
              <thead className="table-dark">
                <tr>
                  <th style={{ minWidth: 220 }}>Feature</th>
                  {plans.map((plan) => (
                    <th key={plan._id} className="text-center">
                      {plan.name}
                      <div className="small font-weight-normal">{plan.tagline}</div>
                    </th>
                  ))}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {formik.values.features.map((feature, index) => (
                  <tr key={feature._id || index}>
                    <td>
                      <div className="font-weight-bold">{feature.name}</div>
                      <div className="small text-muted">{feature.action_key}</div>
                    </td>
                    {plans.map((plan) => {
                      const rule = getRule(feature, plan.slug);
                      return (
                        <td key={plan._id}>
                          <select
                            className="form-control form-control-sm mb-1"
                            value={rule.allowed ? "allow" : "deny"}
                            onChange={(e) =>
                              updateRule(index, plan.slug, {
                                allowed: e.target.value === "allow",
                                quota_type: e.target.value === "allow" ? "unlimited" : "none",
                              })
                            }
                          >
                            <option value="deny">Not allow</option>
                            <option value="allow">Allow</option>
                          </select>
                          {rule.allowed && (
                            <select
                              className="form-control form-control-sm mb-1"
                              value={rule.quota_type === "limited" ? "limited" : "unlimited"}
                              onChange={(e) =>
                                updateRule(index, plan.slug, {
                                  allowed: true,
                                  quota_type: e.target.value,
                                  limit: e.target.value === "limited" ? rule.limit || 1 : null,
                                })
                              }
                            >
                              <option value="unlimited">Unlimited</option>
                              <option value="limited">Limited</option>
                            </select>
                          )}
                          {rule.allowed && rule.quota_type === "limited" && (
                            <input
                              type="number"
                              min="1"
                              className="form-control form-control-sm"
                              placeholder="How many / year"
                              value={rule.limit || ""}
                              onChange={(e) =>
                                updateRule(index, plan.slug, {
                                  allowed: true,
                                  quota_type: "limited",
                                  limit: e.target.value ? Number(e.target.value) : "",
                                })
                              }
                            />
                          )}
                        </td>
                      );
                    })}
                    <td>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => saveOne(feature)}
                      >
                        Save
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button className="btn btn-main-primary my-3" type="submit">
            Save all changes
          </button>
        </form>
      )}
    </div>
  );
};

export default SubscriptionFeature;
